import React from 'react';
import { Box, Paper, Typography, Stack, Chip, Button, Divider } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import { Referral, ReferralStatus } from '~/types/transition';
import { useTransitionContext } from '~/hooks/transition/useTransitionContext';

interface MissingInfoCardProps {
  referral: Referral;
  showAction?: boolean;
}

function formatTimestamp(isoString: string): string {
  return new Date(isoString).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

const MissingInfoCard: React.FC<MissingInfoCardProps> = ({ referral, showAction = true }) => {
  const { simulateMissingInfoResponse } = useTransitionContext();

  if (referral.missingFields.length === 0) return null;

  const hasUnresolvedWithOutreach = referral.missingFields.some(
    (f) => !f.resolved && f.aiOutreachSent
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: 'warning.light' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <WarningAmberIcon color="warning" fontSize="small" />
        <Typography variant="subtitle2" fontWeight={700}>
          Missing Information
        </Typography>
        <Chip
          label={`${referral.missingFields.filter((f) => !f.resolved).length} unresolved`}
          size="small"
          color="warning"
          sx={{ fontSize: '0.65rem', height: 20 }}
        />
      </Stack>

      <Stack spacing={1.5}>
        {referral.missingFields.map((field) => (
          <Box key={field.field}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Typography variant="body2" fontWeight={600}>
                {field.label}
              </Typography>
              {field.resolved ? (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: '0.8rem !important' }} />}
                  label="Resolved"
                  size="small"
                  color="success"
                  sx={{ fontSize: '0.65rem', height: 20 }}
                />
              ) : (
                <Chip
                  icon={<ScheduleIcon sx={{ fontSize: '0.8rem !important' }} />}
                  label="Awaiting"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', height: 20 }}
                />
              )}
            </Stack>
            {field.resolved && field.resolvedValue && (
              <Typography variant="caption" color="success.main">
                Received: {field.resolvedValue}
              </Typography>
            )}
            {!field.resolved && field.aiOutreachSent && field.outreachTimestamp && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                <PhoneCallbackIcon sx={{ fontSize: '0.75rem', color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  AI called referring provider — voicemail left {formatTimestamp(field.outreachTimestamp)}
                </Typography>
              </Stack>
            )}
          </Box>
        ))}
      </Stack>

      {showAction && hasUnresolvedWithOutreach && referral.status === ReferralStatus.AWAITING_INFO && (
        <>
          <Divider sx={{ my: 2 }} />
          <Button
            variant="outlined"
            size="small"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => simulateMissingInfoResponse(referral.id)}
            fullWidth
          >
            Simulate Response Received
          </Button>
        </>
      )}
    </Paper>
  );
};

export default MissingInfoCard;
